import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { SCHOOL_LOGO, SCHOOL_SHORT, ROLE_LABEL } from "@/lib/api";
import { LogOut, Menu, X, ChevronDown, ClipboardCheck } from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import GuruNotifPopup from "@/components/GuruNotifPopup";
import OrtuRunningText from "@/components/OrtuRunningText";

export default function Layout({ children, nav }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [ortuDUVisible, setOrtuDUVisible] = useState(false);

  // Menu Daftar Ulang ortu: hanya muncul bila dibuka admin / ada catatan admin
  useEffect(() => {
    if (user?.role !== "orangtua") { setOrtuDUVisible(false); return; }
    let alive = true;
    api.get("/daftar-ulang/saya")
      .then(({ data }) => { if (alive) setOrtuDUVisible(!!data.visible); })
      .catch(() => {});
    return () => { alive = false; };
  }, [user?.id, user?.role]);

  const navFinal = user?.role === "orangtua" && ortuDUVisible
    ? [nav[0], { to: "/ortu/daftar-ulang", label: "Daftar Ulang", icon: ClipboardCheck }, ...nav.slice(1)]
    : nav;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const renderItem = (item) => {
    if (item.children) {
      const isOpen = expanded[item.label] ?? item.children.some(c => location.pathname === c.to);
      return (
        <div key={item.label}>
          <button
            onClick={() => setExpanded((s) => ({ ...s, [item.label]: !isOpen }))}
            className="w-full flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-lg text-sm text-white/80 hover:text-white hover:bg-white/10 transition-colors"
            data-testid={`nav-group-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <span className="flex items-center gap-3">
              <item.icon className="w-4 h-4" strokeWidth={1.75} />
              <span>{item.label}</span>
            </span>
            <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
          {isOpen && (
            <div className="ml-3 mt-1 space-y-1 border-l border-white/15 pl-3">
              {item.children.map((c) => {
                const active = location.pathname === c.to;
                return (
                  <Link
                    key={c.to}
                    to={c.to}
                    onClick={() => setOpen(false)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${active ? "bg-white text-indigo-700 font-medium" : "text-white/70 hover:text-white hover:bg-white/10"}`}
                    data-testid={`nav-${c.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {c.label}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    }
    const active = location.pathname === item.to;
    return (
      <Link
        key={item.to}
        to={item.to}
        onClick={() => setOpen(false)}
        className={`flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm transition-colors ${active ? "bg-white text-indigo-700 font-semibold" : "text-white/85 hover:text-white hover:bg-white/10"}`}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <item.icon className="w-4 h-4" strokeWidth={1.75} />
        <span>{item.label}</span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="app-layout">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-gradient-to-b from-indigo-600 to-indigo-800 text-white transform transition-transform md:translate-x-0 ${open ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`} data-testid="sidebar">
        <div className="px-5 pt-7 pb-5 border-b border-white/10 flex items-center gap-3">
          <img src={SCHOOL_LOGO} alt="Logo" className="w-12 h-12 rounded-full bg-white p-1 object-contain" />
          <div className="leading-tight">
            <div className="font-bold text-lg">Portal PENA</div>
            <div className="text-[11px] text-white/70">{SCHOOL_SHORT}</div>
          </div>
        </div>
        <nav className="px-3 py-4 space-y-1 overflow-y-auto h-[calc(100vh-200px)]" data-testid="sidebar-nav">
          {navFinal.map(renderItem)}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-white/10 bg-indigo-800/50">
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-rose-500 hover:bg-rose-600 rounded-lg text-sm font-medium transition-colors"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Mobile topbar */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-30 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <button onClick={() => setOpen(true)} data-testid="open-sidebar-btn"><Menu className="w-5 h-5 text-slate-700" /></button>
        <div className="font-semibold text-slate-900">Portal PENA</div>
        <div className="flex items-center gap-1.5" data-testid="mobile-topbar-right">
          <NotificationBell />
          <div
            className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-sm shrink-0"
            title={user?.nama || user?.username}
            data-testid="mobile-topbar-avatar"
          >
            {(user?.nama || user?.username || "?")[0]?.toUpperCase()}
          </div>
        </div>
      </header>
      {open && <div className="md:hidden fixed inset-0 z-30 bg-black/40" onClick={() => setOpen(false)} />}

      <main className="pp-main flex-1 min-w-0 md:ml-64 pt-14 md:pt-0" data-testid="main-content">
        <OrtuRunningText />
        {user?.role === "guru" && <GuruNotifPopup />}
        {/* top bar with user */}
        <div className="hidden md:flex sticky top-0 z-20 bg-white border-b border-slate-200 px-8 py-3 items-center justify-end gap-2">
          <NotificationBell />
          <div className="text-right leading-tight ml-2">
            <div className="text-sm font-semibold text-slate-900" data-testid="topbar-user-name">{user?.nama || user?.username}</div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500">{ROLE_LABEL[user?.role]}</div>
          </div>
          <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold">
            {(user?.nama || user?.username || "?")[0]?.toUpperCase()}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6 sm:py-8 pb-16">
          {children}
        </div>
      </main>
    </div>
  );
}

export function PageTitle({ title, action }) {
  return (
    <div className="mb-6 flex items-end justify-between gap-4 flex-wrap" data-testid="page-title">
      <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{title}</h1>
      {action}
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, color = "indigo", testId }) {
  const colors = {
    indigo: "bg-indigo-50 text-indigo-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    rose: "bg-rose-50 text-rose-600",
    sky: "bg-sky-50 text-sky-600",
  };
  return (
    <div className="pp-card p-5 flex items-center gap-4" data-testid={testId}>
      {Icon && (
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-6 h-6" strokeWidth={1.75} />
        </div>
      )}
      <div>
        <div className="text-3xl font-bold text-slate-900 leading-none">{value}</div>
        <div className="text-sm text-slate-500 mt-1">{label}</div>
      </div>
    </div>
  );
}
